import * as E from 'fp-ts/Either';
import * as TE from 'fp-ts/TaskEither';
import { pipe } from 'fp-ts/lib/function';
import * as Hooks from 'preact/hooks';
import { Api, Fixtuur } from '@fixtuur/backpack';
import * as OptionsActions from '../../../store/actions/options';
import * as Options from '../../../store/options';
import { useDispatch, useSelector } from '../../../store/redux';
import * as Selectors from '../../../store/selectors';
import { useAsyncThrowError } from '../../useAsync';
import { useCachingAsyncCallback } from '../../useCachingAsyncCallback';
/**
 * Necessary because gavel will return options for all properties particles may have
 * In our case we always only care about properties in the whitelist of the current configurator
 * definition.
 *
 * That includes the configurable properties, and the product properties.
 */
const filterOptionsToConfigurator = (
    options: Fixtuur.Configuration.Options.Options,
    configurator: Api.ConfiguratorDefinition.ConfiguratorDefinition,
): Fixtuur.Configuration.Options.Options => {
    const propertyWhitelist = Object.values(configurator.partsMap)
        .flatMap(part => part.properties.concat(part.productProperty))
        .concat(Object.keys(configurator.configurableProperties));
    return Fixtuur.Configuration.Options.filterToNames(propertyWhitelist, options);
};

/**
 * Return value of null indicates an error (failed to retrieve options)
 */
export const useGetConfigurationCompatibleOptions = () => {
    const buildSelections = useSelector(Selectors.Selections.buildPropertySelections);
    const productSelections = useSelector(Selectors.Selections.productSelections);
    const groupingCodeSelections = useSelector(Selectors.Selections.groupingCodeSelections);
    const dispatch = useDispatch();

    const compatibleOptions = useSelector(Selectors.Options.productOptions);

    Hooks.useEffect(() => {
        dispatch(OptionsActions.getCompatibleOptions());
        // TODO: when do we really want to re-run this?
    }, [buildSelections, productSelections, groupingCodeSelections]);

    return {
        compatibleConfigurationOptions: compatibleOptions,
    };
};

/**
 * Return value of null indicates an error (failed to retrieve options)
 */
export const useGetConfigurationAllOptions = () => {
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const groupingCodeSelections = useSelector(Selectors.Selections.groupingCodeSelections);
    const dispatch = useDispatch();

    const getConfigurationOptions = useCachingAsyncCallback(
        async (selections: Api.Configuration.Selections.Selections) => {
            return serviceConfig && currentConfigurator
                ? pipe(
                      Api.Configuration.Options.fromConfigurationSpace(
                          serviceConfig,
                          currentConfigurator.configurationSpaceId,
                          selections,
                      ), // will return all options, even those outside of our current configurator whitelist
                      TE.map(options => Fixtuur.Configuration.Options.fromApi(options)),
                      TE.map(options => filterOptionsToConfigurator(options, currentConfigurator)),
                  )()
                : E.left(null);
        },
        [currentConfigurator, serviceConfig],
    );

    // All possible options
    const allConfigurationOptions = useAsyncThrowError(async () => {
        const apiSelections = Fixtuur.Configuration.Selections.toApi(
            groupingCodeSelections ?? Fixtuur.Configuration.Selections.empty(),
        );
        const options = await getConfigurationOptions(apiSelections);
        const value = E.getOrElseW(e => {
            if (e === null) {
                return null;
            } else {
                throw e;
            }
        })(options);
        if (value) {
            dispatch(Options.setAllOptions(value));
            if (groupingCodeSelections !== null) {
                dispatch(Options.setAllOptionsInitialisedWithGroupingCode(true));
            }
        }
        return value;
    }, [groupingCodeSelections, getConfigurationOptions]);

    return allConfigurationOptions.result;
};

/**
 * Return value of null indicates an error (failed to retrieve options)
 */
export const useGetConfigurationUnlockedOptions = () => {
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);

    const getConfigurationOptions = useCachingAsyncCallback(
        async (selections: Api.Configuration.Selections.Selections) => {
            return serviceConfig && currentConfigurator
                ? pipe(
                      Api.Configuration.Options.fromConfigurationSpace(
                          serviceConfig,
                          currentConfigurator.configurationSpaceId,
                          selections,
                      ), // will return all options, even those outside of our current configurator whitelist
                      TE.map(options => Fixtuur.Configuration.Options.fromApi(options)),
                      TE.map(options => filterOptionsToConfigurator(options, currentConfigurator)),
                  )()
                : E.left(null);
        },
        [currentConfigurator, serviceConfig],
    );

    const allConfigurationOptions = useAsyncThrowError(async () => {
        const options = await getConfigurationOptions({});
        const value = E.getOrElseW(e => {
            if (e === null) {
                return null;
            } else {
                throw e;
            }
        })(options);
        return value;
    }, [getConfigurationOptions]);

    return allConfigurationOptions.result;
};
